import React, { useMemo } from "react";
import {
  Card,
  Badge,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

/**
 * KafkaLagCard – a compact, accessible status card
 *
 * Props
 *  - allDrained: boolean
 *  - totalLagAllGroups: number
 *  - lastUpdated?: string | Date
 *  - thresholds?: { warn: number; critical: number; max?: number }
 *    * warn: upper bound for "OK but catching up"
 *    * critical: upper bound for "degraded" before "critical"
 *    * max: optional max scale for progress (defaults to critical * 2 or 1000)
 */
export default function KafkaLagCard({
  allDrained,
  totalLagAllGroups,
  lastUpdated,
  thresholds = { warn: 100, critical: 1000, max: undefined },
}) {
  const fmt = useMemo(() => new Intl.NumberFormat(undefined), []);

  const state = useMemo(() => {
    const lag = Number(totalLagAllGroups ?? 0);
    const warn = thresholds.warn ?? 100;
    const critical = thresholds.critical ?? 1000;

    // Handle inconsistent input for transparency
    if (allDrained && lag > 0) {
      return {
        label: "Inconsistent",
        subtitle: "Marked drained but lag > 0",
        variant: "secondary",
        dotClass: "bg-secondary",
        pulse: false,
      };
    }

    if (lag === 0 && allDrained) {
      return {
        label: "Drained",
        subtitle: "No consumer lag",
        variant: "success",
        dotClass: "bg-success",
        pulse: false,
      };
    }

    if (lag <= warn) {
      return {
        label: "Catching up",
        subtitle: "Low backlog",
        variant: "warning",
        dotClass: "bg-warning",
        pulse: false,
      };
    }

    if (lag <= critical) {
      return {
        label: "Behind",
        subtitle: "Noticeable backlog",
        variant: "danger",
        dotClass: "bg-danger",
        pulse: true, // subtle attention
      };
    }

    return {
      label: "Critical",
      subtitle: "High backlog",
      variant: "danger",
      dotClass: "bg-danger",
      pulse: true,
    };
  }, [allDrained, totalLagAllGroups, thresholds]);

  const maxScale = useMemo(() => {
    const critical = thresholds.critical ?? 1000;
    return thresholds.max ?? Math.max(critical * 2, 1000);
  }, [thresholds]);

  const progress = Math.min(
    100,
    Math.round((Number(totalLagAllGroups || 0) / maxScale) * 100)
  );

  const updatedText = useMemo(() => {
    if (!lastUpdated) return null;
    const d = new Date(lastUpdated);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleString();
  }, [lastUpdated]);

  return (
    <Card className="flex-fill w-100 shadow-sm border-0">
      <Card.Header className="d-flex align-items-center justify-content-between bg-white">
        <div className="d-flex align-items-center gap-2">
          <span
            className={`status-dot ${state.dotClass} ${
              state.pulse ? "pulse" : ""
            }`}
            aria-hidden
          />
          <Card.Title as="h6" className="mb-0">
            Kafka Lag
          </Card.Title>
        </div>
        <Badge bg={state.variant} aria-label={`Status: ${state.label}`}>
          {state.label}
        </Badge>
      </Card.Header>

      <Card.Body role="status" aria-live="polite" className="pt-3">
        <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-3">
          <div>
            <div className="text-muted small">Total lag (all groups)</div>
            <div
              className="display-6 fw-semibold lh-1"
              title={`${totalLagAllGroups} messages`}
            >
              {fmt.format(Number(totalLagAllGroups || 0))}
            </div>
            <div className="text-muted small mt-1">{state.subtitle}</div>
          </div>

          <div className="flex-grow-1" style={{ minWidth: 200 }}>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="lag-scale-tooltip">
                  Scale anchored to warn ({fmt.format(thresholds.warn)}) and
                  critical ({fmt.format(thresholds.critical)}).
                </Tooltip>
              }
            >
              <div>
                <ProgressBar now={progress} className="mt-2" />
                <div className="d-flex justify-content-between text-muted mt-1 small">
                  <span>0</span>
                  <span>{fmt.format(thresholds.warn)} warn</span>
                  <span>{fmt.format(thresholds.critical)} crit</span>
                  <span>{fmt.format(maxScale)} max</span>
                </div>
              </div>
            </OverlayTrigger>
          </div>
        </div>
      </Card.Body>

      <Card.Footer className="bg-white d-flex justify-content-between align-items-center">
        <small className="text-muted">
          {updatedText ? (
            <>Last updated: {updatedText}</>
          ) : (
            <>Status reflects latest poll</>
          )}
        </small>
        <div className="d-flex gap-2 align-items-center">
          <small className="text-muted">allDrained:</small>
          <Badge bg={allDrained ? "success" : "secondary"}>
            {String(!!allDrained)}
          </Badge>
        </div>
      </Card.Footer>

      <style>{`
        .status-dot {
          width: 12px; height: 12px; border-radius: 50%; display: inline-block;
        }
        .pulse { position: relative; }
        .pulse::after {
          content: ""; position: absolute; inset: 0; border-radius: 50%; opacity: 0.5;
          animation: pulse 1.5s ease-out infinite; background: currentColor;
        }
        @keyframes pulse {
          0% { transform: scale(1); opacity: 0.6; }
          70% { transform: scale(2.1); opacity: 0; }
          100% { transform: scale(2.1); opacity: 0; }
        }
      `}</style>
    </Card>
  );
}
